import { useContext, useState, useEffect } from "react";
import { UserDataContext } from "../components/UserDataContext";
import VN from "../components/VN";
import { useParams, useNavigate } from "react-router-dom";
import { apiRequest } from "../services/apiService";
import {
  Container,
  Button,
  Typography,
  Box,
  Avatar,
  Divider,
  Chip,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import ChecklistIcon from "@mui/icons-material/Checklist";
import MapIcon from "@mui/icons-material/Map";
import FaceIcon from "@mui/icons-material/Face";
import LanguageIcon from "@mui/icons-material/Language";
import CakeIcon from "@mui/icons-material/Cake";
import FlagIcon from "@mui/icons-material/Flag";
import { API_URL } from "../config/api";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";

export default function Portfolio() {
  const { userData } = useContext(UserDataContext);

  const [user, setUser] = useState({});
  const [cities, setCities] = useState([]);
  const [possibilities, setPossibilities] = useState({});
  const [qualifications, setQualifications] = useState({});
  const [experience, setExperience] = useState({});
  const [languages, setLanguages] = useState({});
  const [userText, setUserText] = useState();

  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { id } = useParams();
  const photoUrl = `${API_URL}/get-user-photo/${id}`;

  const handleFinalize = async () => {
    setIsSaving(true);
    try {
      const payload = { status: 2, userId: id };
      console.log("Payload: ", payload);
      const response = await apiRequest("verification_2", "POST", payload);
      if (response.success) {
        setOpenModal(false);

        // Ponowne pobranie danych dla oglądanego profilu (id z URL)
        const userResponse = await apiRequest(`getuser/${id}`, "GET");
        if (userResponse && userResponse.length > 0) {
          setUser(userResponse[0]);
        }
      } else {
        alert(response.message || "Wystąpił błąd.");
      }
    } catch (error) {
      console.error("Błąd połączenia:", error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!userData?.userId) return;
      const isGroup3 = Number(userData.group) === 3;
      const isDifferentUser = String(id) !== String(userData.userId);
      if (isGroup3 && isDifferentUser) {
        navigate(`/portfolio/${userData.userId}`, { replace: true });
        return;
      }

      const targetId = isGroup3 ? userData.userId : id;

      try {
        const response = await apiRequest(`getuser/${targetId}`, "GET");
        if (response && response.length > 0) {
          setUser(response[0]);
          console.log("nanny data:", response[0]);
        }
      } catch (error) {
        console.error("Błąd pobierania użytkownika:", error);
      }
    };
    fetchUser();
  }, [userData, id, navigate]);

  useEffect(() => {
    if (!id) return;

    const fetchProfileDetails = async () => {
      try {
        // 1. Possibilities
        const possibilitiesRes = await apiRequest(
          `get-user-possibilities/${id}`,
          "GET",
        );
        if (possibilitiesRes) {
          setPossibilities(possibilitiesRes[0] || possibilitiesRes);
        }

        // 2. Qualifications
        const qualificationsRes = await apiRequest(
          `get-user-qualifications/${id}`,
          "GET",
        );
        if (qualificationsRes && qualificationsRes.success) {
          setQualifications(qualificationsRes);
        }

        // 3. Experience
        const experienceRes = await apiRequest(
          `get-user-experience/${id}`,
          "GET",
        );
        if (experienceRes && experienceRes.success) {
          setExperience(experienceRes);
        }

        // 4. Languages
        const languagesRes = await apiRequest(
          `get-user-languages/${id}`,
          "GET",
        );
        if (languagesRes && languagesRes.success) {
          setLanguages(languagesRes);
        }

        // 5. Cities
        const citiesRes = await apiRequest(`get-user-cities/${id}`, "GET");
        if (citiesRes && citiesRes.success) {
          setCities(citiesRes.cities || []);
        }

        // 6. User Text
        const textRes = await apiRequest(`get-user-text/${id}`, "GET");
        if (textRes && textRes.length > 0) {
          setUserText(textRes[0]);
        }
      } catch (error) {
        console.error("Błąd pobierania danych szczegółowych profilu:", error);
      }
    };

    fetchProfileDetails();
  }, [id]);

  const groupedCities = cities.reduce((acc, city) => {
    if (!acc[city.voivodeship]) {
      acc[city.voivodeship] = [];
    }
    acc[city.voivodeship].push(city.city);
    return acc;
  }, {});

  const getAgeLabel = (age) => {
    if (age === 1) return "rok";
    if (age % 10 >= 2 && age % 10 <= 4 && (age % 100 < 12 || age % 100 > 14)) {
      return "lata";
    }
    return "lat";
  };

  const currentYear = new Date().getFullYear();
  const age = user.birthyear ? currentYear - user.birthyear : null;

  return (
    <div>
      <VN userData={userData} />
      <Container sx={{ py: 4 }}>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h1"
            fontWeight={800}
            gutterBottom
            sx={{ color: "#1a1a1a", fontSize: { xs: "2rem", md: "3rem" } }}
          >
            Profil zawodowy opiekunki
          </Typography>
        </Box>

        {/* JEDNA KOLUMNA - STACK */}
        <Stack spacing={4}>
          {/* 1. DANE GŁÓWNE OPIEKUNKI */}
          <Card sx={{ position: "relative", overflow: "hidden" }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "80px",
                bgcolor: "primary.main",
                opacity: 0.1,
                zIndex: 0,
              }}
            />
            <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
              <Avatar
                src={photoUrl}
                sx={{
                  width: 150,
                  height: 150,
                  margin: "0 auto 20px",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  border: "4px solid white",
                }}
                imgProps={{ crossOrigin: "use-credentials" }}
              />

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  style={{ textTransform: "capitalize" }}
                >
                  {user.fullname}
                </Typography>

                {age && (
                  <Chip
                    icon={<CakeIcon />}
                    label={`${age} ${getAgeLabel(age)}`}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      borderColor: "primary.light",
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                    }}
                  />
                )}
              </Box>

              <Stack spacing={2} mt={4} alignItems="flex-start">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <FlagIcon color="primary" />
                  <Typography variant="body1">
                    <strong>Obywatelstwo:</strong>{" "}
                    {user.name_pl || "Nie podano"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <LocationOnIcon color="primary" />
                  <Typography variant="body1">
                    <strong>Miejscowość:</strong> {user.city || "Nie podano"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <PhoneIcon color="primary" />
                  <Typography variant="body1">
                    <strong>Telefon:</strong> {user.phone || "Brak numeru"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <FaceIcon color="primary" />
                  <Typography variant="body1">
                    <strong>Krótko o mnie:</strong>{" "}
                    {userText || "Brak informacji..."}
                  </Typography>
                </Box>
              </Stack>

              <Box display="flex" alignItems="flex-start" gap={1.5} mt={2}>
                <LanguageIcon color="primary" />
                <Box>
                  <Typography variant="body1">
                    <strong>Języki:</strong>
                  </Typography>

                  {languages?.data && languages.data.length > 0 ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      mt={1}
                    >
                      {languages.data.map((lang, index) => (
                        <Chip
                          key={index}
                          label={`${lang.language} (${lang.level_name})`}
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            borderColor: "primary.light",
                            bgcolor: "rgba(25, 118, 210, 0.04)",
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      Brak informacji o językach.
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 2. ZAKRES CZYNNOŚCI */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ChecklistIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Co mogę zrobić?
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Box display="flex" flexWrap="wrap" gap={1}>
                {possibilities?.activity &&
                possibilities.activity.length > 0 ? (
                  possibilities.activity.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light",
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak określonego zakresu czynności.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* 3. MIASTA PRACY */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <MapIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  W jakich miastach mogę pracować
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" mb={2}>
                Jestem osobą mobilną i mogę podjąć pracę w następujących
                lokalizacjach:
              </Typography>
              {cities && cities.length > 0 ? (
                Object.entries(groupedCities).map(([voivodeship, cityList]) => (
                  <Box key={voivodeship} mb={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {voivodeship}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {cityList.map((city, index) => (
                        <Chip
                          key={index}
                          label={city}
                          color="primary"
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            p: 1,
                            borderColor: "primary.light",
                            bgcolor: "rgba(25, 118, 210, 0.04)",
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Brak określonych lokalizacji pracy.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 4. DOŚWIADCZENIE */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <WorkIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Doświadczenie
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {experience?.name && experience.name.length > 0 ? (
                  experience.name.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light",
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak doświadczenia..
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* 5. KWALIFIKACJE */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <WorkIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Kwalifikacje
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {qualifications?.description &&
                qualifications.description.length > 0 ? (
                  qualifications.description.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light",
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak kwalifikacji..
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Stack>

        {/* PRZYCISKI AKCYJNE */}
        <Box mt={4} display="flex" gap={2} flexWrap="wrap">
          {userData?.group === 9 && user?.veryf === 1 && (
            <Button
              variant="contained"
              startIcon={<VolunteerActivismIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                flex: 1,
                py: 1.5,
                px: 3,
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "10px",
                textTransform: "none",
                backgroundColor: "#1b5e20",
                boxShadow: "0 4px 12px rgba(46, 125, 50, 0.25)",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                  boxShadow: "0 6px 16px rgba(46, 125, 50, 0.35)",
                },
              }}
            >
              Prześlij do osoby weryfikującej
            </Button>
          )}

          {userData?.group === 2 && user?.veryf === 2 && (
            <Button
              variant="contained"
              startIcon={<VolunteerActivismIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                flex: 1,
                py: 1.5,
                px: 3,
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "10px",
                textTransform: "none",
                backgroundColor: "#1b5e20",
                boxShadow: "0 4px 12px rgba(46, 125, 50, 0.25)",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                  boxShadow: "0 6px 16px rgba(46, 125, 50, 0.35)",
                },
              }}
            >
              Zatwierdź opiekunkę i umieść na liście do wyszukiwania
            </Button>
          )}

          {userData?.group === 2 && user?.veryf === 2 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DisabledByDefaultIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                flex: 1,
                py: 1.5,
                px: 3,
                fontWeight: 600,
                fontSize: "0.95rem",
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#C62828",
                boxShadow: "0 3px 8px rgba(198, 40, 40, 0.22)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#B71C1C",
                  boxShadow: "0 5px 12px rgba(183, 28, 28, 0.3)",
                },
                "&:active": {
                  backgroundColor: "#A31515",
                  boxShadow: "0 2px 4px rgba(183, 28, 28, 0.2)",
                },
              }}
            >
              Odrzuć opiekunkę
            </Button>
          )}
        </Box>

        {/* MODAL -> weryfikacja */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>Potwierdzenie weryfikacji</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Przesłanie do osoby weryfikującej zapisane informacje.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Anuluj</Button>
            <Button
              onClick={handleFinalize}
              variant="contained"
              color="success"
              disabled={isSaving}
            >
              {isSaving ? "Przesyłanie..." : "Potwierdzam i wysyłam"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
